import { Component, DestroyRef, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { TRANSLOCO_SCOPE, TranslocoModule } from '@jsverse/transloco';
import { QaiAlertComponent, QaiButtonComponent } from '@qai/ui';
import { filter } from 'rxjs';
import { CallRefinementPollingService } from 'src/app/services/self-serve/call-refinement-polling.service';
import { mapServerStatusToCallRefinementStatus } from 'src/app/utils/call-refinement-status.util';
import { CallRefinementStatus } from 'src/app/utils/enums/self-serve/call-refinement-status.enum';

/** Matches /self-serve/simulations/:uuid (any tab — GeneratedSimulationInformationComponent
 * only ever renders under the General tab, but its own tab switch destroys/recreates it and
 * dismisses a not-yet-terminal-at-that-point record itself; this is about suppressing the
 * toast while the user is anywhere on that same simulation's details page). */
function extractSimulationUuidFromUrl(url: string): string | null {
  return url.match(/^\/self-serve\/simulations\/([^/?]+)/)?.[1] ?? null;
}

@Component({
  selector: 'call-refinement-banner-host',
  standalone: true,
  templateUrl: './call-refinement-banner-host.component.html',
  styleUrls: ['./call-refinement-banner-host.component.scss'],
  imports: [QaiAlertComponent, QaiButtonComponent, TranslocoModule],
  providers: [{ provide: TRANSLOCO_SCOPE, useValue: 'simulationBuilder' }],
})
export class CallRefinementBannerHostComponent {
  private pollingService = inject(CallRefinementPollingService);
  private router = inject(Router);
  private destroyRef = inject(DestroyRef);

  readonly CallRefinementStatus = CallRefinementStatus;
  readonly ready = signal(false);
  readonly isInSelfServeArea = signal(this.router.url.startsWith('/self-serve'));
  /** The callUuid whose own details page the user is currently on, if any —
   * that page already shows the outcome inline, so its toast is suppressed
   * below rather than shown redundantly alongside it. */
  private readonly currentRouteCallUuid = signal(extractSimulationUuidFromUrl(this.router.url));

  notifications = computed(() =>
    this.pollingService
      .terminalRefinements()
      .filter((job) => job.callUuid !== this.currentRouteCallUuid()),
  );

  constructor() {
    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((event) => {
        this.ready.set(true);
        this.isInSelfServeArea.set(event.urlAfterRedirects.startsWith('/self-serve'));
        this.currentRouteCallUuid.set(extractSimulationUuidFromUrl(event.urlAfterRedirects));
      });
  }

  /** Maps a terminal record's server status + reason to the UI state so the
   * template can branch on Updated vs OutOfScope vs Error without knowing
   * raw server strings. */
  statusOf(serverStatus: string, reason?: string): CallRefinementStatus {
    return mapServerStatusToCallRefinementStatus(serverStatus, reason);
  }

  bannerTitle(job: { serverStatus: string; reason?: string }): string {
    const status = this.statusOf(job.serverStatus, job.reason);
    if (status === CallRefinementStatus.Updated) return 'simulationBuilder.REFINEMENT_APPLIED_TITLE';
    if (status === CallRefinementStatus.OutOfScope) return 'simulationBuilder.REFINEMENT_OUT_OF_SCOPE_TITLE';
    return 'simulationBuilder.REFINEMENT_FAILED_TITLE';
  }

  bannerBody(job: { serverStatus: string; reason?: string }): string {
    const status = this.statusOf(job.serverStatus, job.reason);
    if (status === CallRefinementStatus.Updated) return 'simulationBuilder.REFINEMENT_APPLIED_BODY';
    if (status === CallRefinementStatus.OutOfScope) return 'simulationBuilder.REFINEMENT_OUT_OF_SCOPE_BODY';
    return 'simulationBuilder.REFINEMENT_FAILED_BODY';
  }

  dismiss(callUuid: string): void {
    this.pollingService.dismiss(callUuid);
  }

  handleViewSimulationClick(callUuid: string, moduleUuid?: string, simulationStatus?: string): void {
    this.dismiss(callUuid);
    // moduleUuid is required by SimulationDetailsComponent's own fetch —
    // without it, that page's data-fetch hangs and never renders (including
    // its breadcrumbs). status is read by MessagingThemeComponent to know
    // whether the simulation is already published. Same query params the
    // existing simulation-generation banner's equivalent navigation sends.
    this.router.navigate(['/self-serve/simulations', callUuid], {
      queryParams: { moduleUuid, status: simulationStatus },
    });
  }
}
